import client from './client';
import {
  ReportsConfig,
  ReportRecipient,
  ReportOpsManager,
  ReportEmployeeResult,
  RecipientType,
  ReportQuestion,
  TimesheetQuestionOption,
  ReportRole,
} from '../interfaces/ReportsConfiguration';

/**
 * Reports Configuration admin API client. Mirrors the API surface in
 * src/routes/admin/reportsConfig.ts. Per-endpoint error-message helpers follow the
 * punchExceptions.ts pattern (dig response.data.error / .message, else Error.message,
 * else a per-call fallback). The page refetches GET /config after every successful
 * mutation, so the mutation responses are returned but the page does not rely on them.
 */

/** Shared extractor: prefer the server's error/message, then Error.message, then fallback. */
function reportsErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string; message?: string } } }).response;
    if (response?.data?.error) return response.data.error;
    if (response?.data?.message) return response.data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

// ── GET /admin/reports/config ────────────────────────────────────────────────

export function getReportsConfigErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while loading the reports configuration.');
}

export async function getReportsConfig(): Promise<ReportsConfig> {
  const response = await client.get<ReportsConfig>('admin/reports/config');
  return response.data;
}

// ── PATCH /admin/reports/config/:rptReportID ─────────────────────────────────

export function patchReportErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while updating the report.');
}

export interface PatchReportFlags {
  active?: boolean;
  ccRosterSupervisors?: boolean;
  notifySupervisorChain?: boolean;
  /**
   * Explicit null CLEARS the stop role, which is a real state ("walk to the top"),
   * so this is sent whenever the key is present rather than when it is truthy.
   */
  supervisorChainStopRoleID?: number | null;
}

export async function patchReport(
  rptReportID: number,
  flags: PatchReportFlags,
): Promise<{ rptReportID: number } & PatchReportFlags> {
  const response = await client.patch(`admin/reports/config/${rptReportID}`, flags);
  return response.data;
}

// ── POST /admin/reports/config/:rptReportID/recipients ───────────────────────

export function addRecipientErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while adding the recipient.');
}

/** Exactly one of email / employeeID (one-source recipient rows). */
export interface AddRecipientInput {
  recipientType: RecipientType;
  email?: string;
  employeeID?: number;
}

export async function addRecipient(
  rptReportID: number,
  input: AddRecipientInput,
): Promise<ReportRecipient> {
  const response = await client.post<ReportRecipient>(
    `admin/reports/config/${rptReportID}/recipients`,
    input,
  );
  return response.data;
}

// ── DELETE /admin/reports/recipients/:rptReportRecipientID ────────────────────

export function removeRecipientErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while removing the recipient.');
}

export async function removeRecipient(rptReportRecipientID: number): Promise<void> {
  await client.delete(`admin/reports/recipients/${rptReportRecipientID}`);
}

// ── Trigger questions (TAS-123) ──────────────────────────────────────────────

export function reportQuestionsErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while loading the questions.');
}

export function addReportQuestionErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while adding the trigger question.');
}

export function patchReportQuestionErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while updating the trigger question.');
}

export function removeReportQuestionErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while removing the trigger question.');
}

export function reportRolesErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while loading the roles.');
}

/** GET /admin/reports/timesheetQuestions — the add-question picker source. */
export async function getTimesheetQuestionOptions(
  includeInactive = true,
): Promise<TimesheetQuestionOption[]> {
  const response = await client.get<{ questions: TimesheetQuestionOption[] }>(
    'admin/reports/timesheetQuestions',
    { params: includeInactive ? { includeInactive: 1 } : {} },
  );
  return response.data.questions ?? [];
}

/** GET /admin/reports/roles — the supervisor-chain stop picker source. */
export async function getReportRoles(): Promise<ReportRole[]> {
  const response = await client.get<{ roles: ReportRole[] }>('admin/reports/roles');
  return response.data.roles ?? [];
}

/** POST — select a question as a trigger. `triggerAnswer` null = any answer. */
export async function addReportQuestion(
  rptReportID: number,
  input: { timesheetQuestionsID: number; triggerAnswer: string | null },
): Promise<ReportQuestion> {
  const response = await client.post<ReportQuestion>(
    `admin/reports/config/${rptReportID}/questions`,
    input,
  );
  return response.data;
}

/**
 * PATCH — change the trigger value and/or the escalation flag. Both are optional;
 * send only what changed. Saves immediately, there is no dirty state on the panel.
 */
export async function patchReportQuestion(
  rptReportQuestionID: number,
  input: { triggerAnswer?: string | null; notifySupervisorChain?: boolean },
): Promise<ReportQuestion> {
  const response = await client.patch<ReportQuestion>(
    `admin/reports/questions/${rptReportQuestionID}`,
    input,
  );
  return response.data;
}

/** DELETE — soft-remove a trigger selection. */
export async function removeReportQuestion(rptReportQuestionID: number): Promise<void> {
  await client.delete(`admin/reports/questions/${rptReportQuestionID}`);
}

// ── POST /admin/reports/config/:rptReportID/opsManagers ──────────────────────

export function enrollManagerErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while enrolling the manager.');
}

/**
 * Per-manager extras (NPM Daily / Weekly Ops): addresses copied on that manager's
 * email, an alternate report title, the name the email shows for the manager, and
 * the distribution group that combines several managers into one email.
 * Explicit null on alternateTitle / displayName CLEARS it, so the key is sent
 * whenever present rather than when truthy.
 */
export interface ManagerExtras {
  ccEmails?: string[];
  alternateTitle?: string | null;
  displayName?: string | null;
  /**
   * "Combine into email" group name; managers sharing one get a single email.
   * PATCH: omit = unchanged, null = clear (sends its own email again).
   */
  distributionName?: string | null;
}

export async function enrollManager(
  rptReportID: number,
  employeeID: number,
  extras?: ManagerExtras,
): Promise<ReportOpsManager> {
  const response = await client.post<ReportOpsManager>(
    `admin/reports/config/${rptReportID}/opsManagers`,
    { employeeID, ...(extras ?? {}) },
  );
  return response.data;
}

// ── PATCH /admin/reports/opsManagers/:rptReportOpsManagerID ───────────────────

export function patchManagerErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while updating the manager.');
}

/** Returns the updated ops manager with its roster. */
export async function patchManager(
  rptReportOpsManagerID: number,
  extras: ManagerExtras,
): Promise<ReportOpsManager> {
  const response = await client.patch<ReportOpsManager>(
    `admin/reports/opsManagers/${rptReportOpsManagerID}`,
    extras,
  );
  return response.data;
}

// ── DELETE /admin/reports/opsManagers/:rptReportOpsManagerID ──────────────────

export function removeManagerErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while removing the manager.');
}

export async function removeManager(rptReportOpsManagerID: number): Promise<void> {
  await client.delete(`admin/reports/opsManagers/${rptReportOpsManagerID}`);
}

// ── GET /admin/reports/run/:reportKey/excel ──────────────────────────────────

/** Which pay period an on-demand run covers. Resolved server-side, not by PayPeriodID math here. */
export type ReportRunScope = 'current' | 'prior';

/**
 * What an on-demand run should cover: a relative scope the engine resolves, or one
 * explicit pay period. Exactly one of the two — they are different query parameters
 * server-side and sending both would leave the winner up to the engine.
 */
export type ReportRunTarget =
  | { scope: ReportRunScope }
  | { payPeriodID: number }
  | { month: number; year: number };

/**
 * Unlike the other error helpers this one is async, and it has to be: with
 * responseType 'blob' the *error* body is a Blob too, so reportsErrorMessage can't see
 * response.data.message and every failure would read as a generic fallback.
 */
export async function downloadReportExcelErrorMessage(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { error?: string; message?: string };
      if (parsed.error) return parsed.error;
      if (parsed.message) return parsed.message;
      if (text.trim()) return text.trim().slice(0, 300);
    } catch {
      // Not JSON — fall through to the shared extractor.
    }
  }
  return reportsErrorMessage(err, 'An error occurred while building the report file.');
}

/** Pulls the filename out of Content-Disposition so ReportEngine's week number carries through. */
function filenameFromDisposition(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Runs a report on demand and saves the workbook. The API proxies ReportEngine, so this is
 * the same query the scheduled email uses. Download mechanics mirror generateSageExport
 * in api/2020ph.ts.
 */
export async function downloadReportExcel(
  reportKey: string,
  target: ReportRunTarget,
): Promise<void> {
  const params = 'payPeriodID' in target
    ? { payPeriodID: target.payPeriodID }
    : 'month' in target
      ? { month: target.month, year: target.year }
      : { scope: target.scope };

  const response = await client.get(`admin/reports/run/${reportKey}/excel`, {
    params,
    responseType: 'blob',
  });

  // Monthly PRDA / PW reports come back as a .zip of several workbooks.
  const contentType = String(response.headers['content-type'] ?? '').toLowerCase();
  const filename = filenameFromDisposition(
    response.headers['content-disposition'],
    `${reportKey}${contentType.includes('zip') ? '.zip' : '.xlsx'}`,
  );

  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

// ── GET /admin/reports/employeeSearch?q= ─────────────────────────────────────

export function searchReportEmployeesErrorMessage(err: unknown): string {
  return reportsErrorMessage(err, 'An error occurred while searching employees.');
}

export async function searchReportEmployees(q: string): Promise<ReportEmployeeResult[]> {
  const response = await client.get<{ results: ReportEmployeeResult[] }>(
    'admin/reports/employeeSearch',
    { params: { q } },
  );
  return response.data.results;
}
